import { useAuthStatus } from '@/hooks';
import { useRoleLabel } from '@/hooks/useRoleLabel';
import { useRoles } from '@/hooks/useRoles';
import { useAppStore } from '@/stores/app';
import { Dialog } from '@nautilusbio/nb-design-system/Dialog';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { UserRound } from 'lucide-react';
import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import sidebarStyles from '../Sidebar/styles.module.css';

interface ProfileProps {
  id: string;
  title?: string;
}

/**
 * Read-only account summary. Everything shown already arrives on `/auth/me`, so this needs no
 * request of its own; there is no editable-profile endpoint to back anything more than this.
 */
const Profile: FC<ProfileProps> = ({ id, title }) => {
  const { t } = useTranslation('sidebar');
  const { user } = useAuthStatus();
  const selectedOrg = useAppStore(state => state.selectedOrganization);
  const { getCurrentRole } = useRoles();
  const roleLabel = useRoleLabel();

  const label = title ?? t('profile');
  const currentRole = selectedOrg ? getCurrentRole(selectedOrg.id) : undefined;

  const triggerContent = useMemo(
    () => (
      <>
        <UserRound size={14} /> {label}
      </>
    ),
    [label],
  );

  const dialogContent = useMemo(
    () => (
      <Stack as="section" gap={4} orientation="vertical">
        <Stack gap={1} orientation="vertical">
          <Text as="span" size="sm">
            {t('profile_name')}
          </Text>

          <Text as="p" data-testid="profile-full-name" size="sm">
            {user?.full_name}
          </Text>
        </Stack>

        <Stack gap={1} orientation="vertical">
          <Text as="span" size="sm">
            {t('profile_email')}
          </Text>

          <Text as="p" data-testid="profile-email" size="sm">
            {user?.email}
          </Text>
        </Stack>

        {selectedOrg && (
          <Stack gap={1} orientation="vertical">
            <Text as="span" size="sm">
              {t('profile_role', { org: selectedOrg.name })}
            </Text>

            <Text as="p" data-testid="profile-role" size="sm">
              {currentRole ? roleLabel(currentRole) : t('profile_no_role')}
            </Text>
          </Stack>
        )}
      </Stack>
    ),
    [t, user?.full_name, user?.email, selectedOrg, currentRole, roleLabel],
  );

  // Dialog derives its own suffixed test ids, so a bare `profile` yields `profile-dialog-trigger-btn`
  // rather than doubling up on "-dialog".
  return (
    <Dialog
      classNames={{ overlay: sidebarStyles.dialogOverlay }}
      content={dialogContent}
      data-testid={id}
      title={label}
      triggerProps={{
        children: triggerContent,
        fullWidth: true,
        size: 'sm',
        visual: 'default',
      }}
    />
  );
};

Profile.displayName = 'Profile';

export default Profile;
